import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Circle, Briefcase } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Parses a fenced [ACTION_ITEMS] block from Papaya's text. Returns { cleanText, items } or null.
export function parseActionItems(text) {
  if (!text) return null;
  const re = /\[ACTION_ITEMS\]\s*([\s\S]*?)\s*\[\/ACTION_ITEMS\]/i;
  const m = text.match(re);
  if (!m) return null;
  const block = m[1];
  const items = block
    .split('\n')
    .map((line) => line.trim().replace(/^[-*\u2022]\s*/, '').trim())
    .filter((line) => line.length > 0);
  if (items.length === 0) return null;
  const cleanText = text.replace(re, '').trim();
  return { cleanText, items };
}

const STORAGE_KEY = (sessionId, signature) => `actionItems_${sessionId}_${signature}`;

const POMEGRANATE = "\u{1F343}"; // we'll use seed icon below — keep this as a fallback
const POMEGRANATE_EMOJI = "\u{1FAD8}"; // pomegranate fallback emoji

const ActionItemsCard = ({ items, sessionId, currentTier, onTierGraduate }) => {
  const signature = items.join('|').slice(0, 80).replace(/\W/g, '_');
  const storageKey = STORAGE_KEY(sessionId || 'anon', signature);

  const [checked, setChecked] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      return saved ? JSON.parse(saved) : Array(items.length).fill(false);
    } catch {
      return Array(items.length).fill(false);
    }
  });
  const [seedFlash, setSeedFlash] = useState(null);
  const [blueberryFlash, setBlueberryFlash] = useState(false);
  const [garmentFlash, setGarmentFlash] = useState(false);

  useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify(checked)); } catch { /* ignore */ }
  }, [checked, storageKey]);

  // Level 28-29 (tiers 28-34, internal index 27-33) graduates by ALL items checked, no blueberry cycle.
  const isActionItemTier = typeof currentTier === 'number' && currentTier >= 27 && currentTier <= 33;
  const allChecked = checked.every(Boolean) && checked.length > 0;

  useEffect(() => {
    if (!isActionItemTier || !allChecked || !sessionId) return;
    // Fire-and-forget the tier-graduation. The chat will refresh on next /api/chat call.
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/action-items/complete-tier/${sessionId}`, { method: 'POST' });
        const data = await res.json();
        if (cancelled) return;
        if (data.ok && data.tier_graduated) {
          setGarmentFlash(true);
          setTimeout(() => setGarmentFlash(false), 5000);
          if (typeof onTierGraduate === 'function') onTierGraduate(data);
        }
      } catch (err) {
        console.warn('[ActionItems] tier-complete fetch failed:', err?.message || err);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allChecked, isActionItemTier, sessionId]);

  const toggle = async (idx) => {
    const wasChecked = checked[idx];
    const newChecked = checked.map((v, i) => (i === idx ? !v : v));
    setChecked(newChecked);

    try {
      if (!wasChecked) {
        // Check: add a pomegranate seed (Level 28 tiers still award seeds — extra ladders to next blueberry post-graduation)
        const res = await fetch(`${API}/pomegranate/seed/${sessionId}`, { method: 'POST' });
        const data = await res.json();
        if (data.ok) {
          setSeedFlash(`+1 seed (${data.seeds_to_next_blueberry} more for a blueberry)`);
          setTimeout(() => setSeedFlash(null), 2500);
          if (data.blueberry_awarded) {
            setBlueberryFlash(true);
            setTimeout(() => setBlueberryFlash(false), 4500);
          }
        }
      } else {
        // Uncheck: remove a pomegranate seed
        await fetch(`${API}/pomegranate/unseed/${sessionId}`, { method: 'POST' });
      }
    } catch (err) {
      console.warn('[ActionItems] seed/unseed network error:', err?.message || err);
    }
  };

  const completedCount = checked.filter(Boolean).length;
  const allDone = completedCount === items.length;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ type: "spring", damping: 18, stiffness: 220 }}
      className="rounded-2xl p-4 mt-2 mb-1 relative"
      style={{
        background: "linear-gradient(135deg, #FFF8E7 0%, #F8E8C8 100%)",
        border: "1.5px solid #D4A574",
        boxShadow: "0 8px 22px rgba(212, 165, 116, 0.25)"
      }}
      data-testid="action-items-card"
    >
      <div className="flex items-center gap-2 mb-2">
        <div
          className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: "linear-gradient(135deg, #D4A574, #B8895A)" }}
        >
          <Briefcase size={13} style={{ color: "#FFF" }} strokeWidth={2.5} />
        </div>
        <div className="flex-1">
          <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: "#8B6C3F", fontFamily: "'Cinzel', serif" }}>
            Workers&rsquo; Lunchbox
          </div>
          <div className="text-sm font-bold" style={{ color: "#3D2A0E", fontFamily: "'Fredoka', sans-serif" }}>
            Your {items.length} Action Items for the Break
          </div>
        </div>
        <div
          className="text-[10px] font-bold px-2 py-1 rounded-full"
          style={{
            backgroundColor: allDone ? "#4CAF50" : "#FFF",
            color: allDone ? "#FFF" : "#8B6C3F",
            border: allDone ? "none" : "1px solid #D4A574"
          }}
          data-testid="action-items-progress"
        >
          {completedCount}/{items.length}
        </div>
      </div>

      <p className="text-[10px] italic mb-3 flex items-center gap-1" style={{ color: "#8B6C3F", fontFamily: "'Nunito', sans-serif" }}>
        <span aria-hidden="true">{POMEGRANATE_EMOJI}</span>
        Each task you complete grants 1 pomegranate seed. 5 seeds = 1 blueberry on your return.
      </p>

      <ul className="space-y-2">
        {items.map((item, idx) => (
          <li key={`${idx}-${item.slice(0, 20)}`}>
            <button
              type="button"
              onClick={() => toggle(idx)}
              className="w-full flex items-start gap-2 text-left rounded-lg px-2 py-1.5 transition hover:bg-white/40"
              data-testid={`action-item-${idx}`}
            >
              {checked[idx] ? (
                <CheckCircle2 size={18} style={{ color: "#4CAF50", flexShrink: 0, marginTop: 1 }} strokeWidth={2.5} />
              ) : (
                <Circle size={18} style={{ color: "#B8895A", flexShrink: 0, marginTop: 1 }} strokeWidth={2.5} />
              )}
              <span
                className="text-xs leading-snug flex-1"
                style={{
                  color: "#3D2A0E",
                  fontFamily: "'Nunito', sans-serif",
                  textDecoration: checked[idx] ? "line-through" : "none",
                  opacity: checked[idx] ? 0.55 : 1
                }}
              >
                {item}
              </span>
              {checked[idx] && (
                <span className="text-sm flex-shrink-0" aria-hidden="true">{POMEGRANATE_EMOJI}</span>
              )}
            </button>
          </li>
        ))}
      </ul>

      {/* Seed flash */}
      {seedFlash && (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          className="absolute top-2 right-2 px-2 py-1 rounded-full text-[10px] font-bold"
          style={{ background: "linear-gradient(135deg, #C84B5A, #E07A5F)", color: "#FFF8E7" }}
          data-testid="pomegranate-flash"
        >
          {POMEGRANATE_EMOJI} {seedFlash}
        </motion.div>
      )}

      {/* Blueberry awarded */}
      {blueberryFlash && (
        <motion.div
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          className="text-[11px] font-bold text-center mt-3 py-1.5 rounded-full"
          style={{ background: "linear-gradient(135deg, #4F378B, #7B5EA7)", color: "#FFF" }}
          data-testid="blueberry-from-seeds"
        >
          {"\u{1FAD0}"} 5 seeds bloomed into 1 blueberry! {"\u2728"}
        </motion.div>
      )}

      {allDone && (
        <motion.div
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-[11px] font-semibold text-center mt-3 italic"
          style={{ color: "#2E7D32", fontFamily: "'Nunito', sans-serif" }}
        >
          {"\u2728"} All revelations carried into the world. Azap is pleased.
        </motion.div>
      )}
    </motion.div>
  );
};

export default ActionItemsCard;
